import * as React from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, FormGroup, Label, FormFeedback } from "reactstrap";

import { NivelEducativoDependenciasModel, NivelEducativoDestinoListItem } from "../models/dataModels";
import { ComponentBase } from "../utils/base/ComponentBase";
import { DeleteIcon } from "../utils/Icons";
import { I18n } from "../utils/I18n";
import { NivelDestinoDropDown } from "./NivelDestinoDropDown";

interface MoverEstudiantesNivelModalProps {
    /** Lo que cuelga del nivel que se quiere borrar, tal como lo ha contado el servidor. */
    dependencias: NivelEducativoDependenciasModel;

    /** Niveles que no se pueden elegir como destino: el que se borra y los demás marcados para borrar. */
    excluirNivelIds: number[];

    onCancel: () => void;

    /** Se llama con el nivel elegido; el borrado y el traslado se aplican al guardar el curso escolar. */
    onConfirm: (nivelDestinoId: number) => void;
}

interface MoverEstudiantesNivelModalState {
    nivelDestinoId: number;
    nivelDestino: NivelEducativoDestinoListItem;
    mostrarErrorDestino: boolean;
}

/**
 * Explica por qué un nivel educativo no se puede borrar sin más y, cuando el motivo son los
 * estudiantes de la tienda, permite elegir a qué nivel se mueven.
 *
 * Tiene dos modos según lo que cuelgue del nivel:
 * - bloqueado (hay matrículas o usuarios con ese curso como curso actual): solo informa, porque son
 *   datos académicos del colegio que hay que resolver antes en sus pantallas;
 * - mover: pide el nivel de destino de los alumnos de la tienda, que nunca se eliminan.
 */
export class MoverEstudiantesNivelModal extends ComponentBase<MoverEstudiantesNivelModalProps, MoverEstudiantesNivelModalState> {

    constructor(props: MoverEstudiantesNivelModalProps) {
        super(props);

        this.state = {
            nivelDestinoId: null,
            nivelDestino: null,
            mostrarErrorDestino: false
        };
    }

    render() {

        var dependencias = this.props.dependencias;
        var bloqueado = dependencias.borradoBloqueado;

        return <Modal isOpen={true} className="wide-dialog">
            <ModalHeader toggle={() => this.props.onCancel()}>
                {bloqueado
                    ? I18n.Strings.nivelEducativo.borradoBloqueadoTitulo
                    : I18n.Strings.nivelEducativo.moverEstudiantesTitulo}
            </ModalHeader>
            <ModalBody>
                {bloqueado
                    ? this._renderBloqueado()
                    : this._renderMover()}
            </ModalBody>
            <ModalFooter>
                {bloqueado
                    ? <Button color="primary" onClick={() => this.props.onCancel()}>
                        {I18n.Strings.ok}
                    </Button>
                    : <React.Fragment>
                        <Button color="primary" onClick={() => this._confirmar()}>
                            <DeleteIcon /> {I18n.Strings.nivelEducativo.moverYBorrar}
                        </Button>
                        <Button color="secondary" onClick={() => this.props.onCancel()}>
                            {I18n.Strings.cancel}
                        </Button>
                    </React.Fragment>}
            </ModalFooter>
        </Modal>;
    }

    /** Pinta el detalle de los datos académicos que impiden borrar el nivel. */
    private _renderBloqueado(): JSX.Element {

        var dependencias = this.props.dependencias;

        var motivos = [] as string[];
        if (dependencias.matriculas > 0) {
            motivos.push(I18n.Strings.nivelEducativo.borradoBloqueadoMatriculas(dependencias.matriculas, dependencias.matriculasDeUsuariosDeBaja));
        }
        if (dependencias.alumnosConClaseActual > 0) {
            motivos.push(I18n.Strings.nivelEducativo.borradoBloqueadoClaseActual(dependencias.alumnosConClaseActual, dependencias.alumnosConClaseActualDeBaja));
        }

        return <React.Fragment>
            <p>{I18n.Strings.nivelEducativo.borradoBloqueadoTexto(dependencias.nombre)}</p>
            <ul>
                {motivos.map((motivo, indice) => <li key={indice}>{motivo}</li>)}
                {dependencias.estudiantesTienda > 0 &&
                    <li>{I18n.Strings.nivelEducativo.moverEstudiantesTexto(dependencias.estudiantesTienda, dependencias.nombre)}</li>}
            </ul>
            <p>{I18n.Strings.nivelEducativo.borradoBloqueadoComoDesbloquear}</p>
        </React.Fragment>;
    }

    /** Pide el nivel de destino de los estudiantes de la tienda. */
    private _renderMover(): JSX.Element {

        var dependencias = this.props.dependencias;

        return <React.Fragment>
            <p>
                {I18n.Strings.nivelEducativo.moverEstudiantesTexto(dependencias.estudiantesTienda, dependencias.nombre)}
                {" "}
                {I18n.Strings.nivelEducativo.moverEstudiantesNoSeEliminan}
            </p>
            <FormGroup>
                <Label for="nivelDestino">{I18n.Strings.nivelEducativo.nivelDestino}</Label>
                <NivelDestinoDropDown
                    id="nivelDestino"
                    value={this.state.nivelDestinoId}
                    excluirNivelIds={this.props.excluirNivelIds}
                    invalid={this.state.mostrarErrorDestino}
                    onChange={(nivelEducativoId, nivel) => {
                        this.setState({
                            nivelDestinoId: nivelEducativoId,
                            nivelDestino: nivel,
                            mostrarErrorDestino: false
                        });
                    }}
                />
                {this.state.mostrarErrorDestino &&
                    <FormFeedback className="d-block">{I18n.Strings.nivelEducativo.nivelDestinoRequerido}</FormFeedback>}
            </FormGroup>
            {this.state.nivelDestino != null && !this.state.nivelDestino.cursoEscolarActivo &&
                <p className="text-danger">{I18n.Strings.nivelEducativo.avisoDestinoOtroCursoEscolar}</p>}
            {(dependencias.clases > 0 || dependencias.materias > 0 || dependencias.contenidos > 0) &&
                <p>{I18n.Strings.nivelEducativo.seBorraranEnCascada(dependencias.clases, dependencias.materias, dependencias.contenidos)}</p>}
        </React.Fragment>;
    }

    /** Valida el destino elegido y devuelve el control al formulario del curso escolar. */
    private _confirmar() {

        if (this.state.nivelDestinoId == null) {
            this.setState({ mostrarErrorDestino: true });
            return;
        }

        this.props.onConfirm(this.state.nivelDestinoId);
    }
}
